import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, CardContent, Alert, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS, buildDialpadPath } from "@/config/apiConstants";
import { pharmacyHaloDataGridSx } from "@/utils/adminTableStyles";
import AdminBackLink from "@/components/admin/dialpad/AdminBackLink";

const DIALPAD_PINK = "#E63888";

const AdminDialpadUsersPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await phappsRequest(API_PATHS.DIALPAD_ADMIN_USERS, "GET");
      setRows((data ?? []).map(r => ({ ...r, id: r.Id })));
    } catch {
      setError("Failed to load user mapping");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (row) => {
    setEditing(row);
    setInputValue(row.dialpad_user_id != null ? String(row.dialpad_user_id) : "");
    setSaveError(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const dialpad_user_id = inputValue.trim() === "" ? null : Number(inputValue.trim());
      await phappsRequest(buildDialpadPath(`admin/users/${editing.Id}/link`), "POST", { dialpad_user_id });
      setEditing(null);
      await load();
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { field: "Name", headerName: "Name", flex: 1, minWidth: 140 },
    { field: "Email", headerName: "Email", flex: 1, minWidth: 180 },
    {
      field: "dialpad_user_id", headerName: "Dialpad User ID", width: 180,
      renderCell: p => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {p.value != null ? String(p.value) : <span style={{ color: "#9ca3af" }}>Not linked</span>}
        </Typography>
      ),
    },
    {
      field: "actions", headerName: "", width: 110, sortable: false,
      renderCell: p => (
        <Button
          size="small"
          startIcon={p.row.dialpad_user_id != null ? <LinkOffIcon /> : <LinkIcon />}
          onClick={() => openEdit(p.row)}
          sx={{ textTransform: "none", color: DIALPAD_PINK, fontWeight: 600 }}
        >
          {p.row.dialpad_user_id != null ? "Edit" : "Link"}
        </Button>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <AdminBackLink sx={{ mb: 1 }} />
      <Typography variant="h5" sx={{ fontWeight: 800, color: DIALPAD_PINK, mb: 0.5 }}>
        Dialpad User Mapping
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Link PHAPPS managers to their Dialpad user IDs. Calls are auto-linked by email on first match.
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Card sx={{ borderRadius: 2, border: "1px solid #e5e7eb", boxShadow: "none" }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: Math.min(600, 56 + rows.length * 36 + 52), width: "100%" }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableColumnMenu
              disableRowSelectionOnClick
              hideFooter={rows.length <= 25}
              density="compact"
              sx={{ ...pharmacyHaloDataGridSx, "& .MuiDataGrid-columnHeader": { backgroundColor: "#F58A2E", color: "#fff" }, "& .MuiDataGrid-row:hover": { backgroundColor: "#FFFBEB !important" } }}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: DIALPAD_PINK }}>
          {editing?.dialpad_user_id != null ? "Edit" : "Link"} Dialpad ID — {editing?.Name}
        </DialogTitle>
        <DialogContent>
          {saveError && <Alert severity="warning" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Enter the Dialpad user ID (numeric). Leave blank to unlink.
          </Typography>
          <TextField
            fullWidth size="small" label="Dialpad User ID"
            value={inputValue} onChange={e => setInputValue(e.target.value)}
            placeholder="e.g. 9876543210"
            inputProps={{ inputMode: "numeric" }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditing(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained" disabled={saving} onClick={handleSave}
            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: DIALPAD_PINK }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDialpadUsersPage;
